import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * 3D Rotation — FTUE demo (3 essais progressive intro).
 *
 * Spec adaptée MVP corpus 30 items :
 *   E1 : 30° (rang ~2), identique. Slow.
 *   E2 : 60° (rang ~4), miroir.
 *   E3 : 90° (rang 6), baseline.
 *
 * MVP — SVG iso projections ; full WebGL/three.js Sprint 4+.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ROTATION_CORPUS } from './corpus';
const DEMO_STEPS = [
    {
        idx: 1,
        titleKey: 'rotation_3d.demo.step1_title',
        hintKey: 'rotation_3d.demo.step1_hint',
        angleDeg: 45,
        wantIdentical: true,
    },
    {
        idx: 2,
        titleKey: 'rotation_3d.demo.step2_title',
        hintKey: 'rotation_3d.demo.step2_hint',
        angleDeg: 45,
        wantIdentical: false,
    },
    {
        idx: 3,
        titleKey: 'rotation_3d.demo.step3_title',
        hintKey: 'rotation_3d.demo.step3_hint',
        angleDeg: 90,
        wantIdentical: true,
    },
];
const SHAPE_VIEWBOX = 80;
const SHAPE_RENDER = 120;
const STAGE_BG = '#f0f7fc';
const SHAPE_FILL = '#0d2a4a';
const SHAPE_STROKE = '#0d2a4a';
function pickItem(angleDeg, wantIdentical) {
    const candidates = ROTATION_CORPUS.filter((i) => i.angleDeg === angleDeg && i.isIdentical === wantIdentical);
    if (candidates.length > 0)
        return candidates[0];
    // Fallback lâche.
    const loose = ROTATION_CORPUS.filter((i) => i.angleDeg === angleDeg);
    if (loose.length > 0)
        return loose[0];
    return ROTATION_CORPUS[0];
}
export function Demo({ level: _level, onComplete, onSkip }) {
    const { t } = useTranslation(['games', 'common']);
    const [stepIdx, setStepIdx] = useState(0);
    const [pick, setPick] = useState(null);
    const step = DEMO_STEPS[stepIdx];
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    const item = useMemo(() => pickItem(step.angleDeg, step.wantIdentical), [step.angleDeg, step.wantIdentical]);
    const correctPick = item.isIdentical ? 'identical' : 'mirror';
    const correct = pick === correctPick;
    const handlePick = (p) => {
        if (pick !== null)
            return;
        setPick(p);
    };
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setStepIdx((i) => i + 1);
        setPick(null);
    };
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: t('common:demo.eyebrow_with_master', { master: t('rotation_3d.demo.master') }) }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: t(step.titleKey) }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: t(step.hintKey) }), _jsxs("div", { className: "flex items-center justify-center gap-s3 border border-cyan-border-faint", style: { background: STAGE_BG, padding: '12px' }, children: [_jsx("svg", { width: SHAPE_RENDER, height: SHAPE_RENDER, viewBox: `0 0 ${SHAPE_VIEWBOX} ${SHAPE_VIEWBOX}`, "aria-label": t('rotation_3d.aria.reference'), role: "img", children: _jsx("polygon", { points: item.refPoints, fill: SHAPE_FILL, stroke: SHAPE_STROKE, strokeWidth: 1.5, strokeLinejoin: "round" }) }), _jsx("svg", { width: SHAPE_RENDER, height: SHAPE_RENDER, viewBox: `0 0 ${SHAPE_VIEWBOX} ${SHAPE_VIEWBOX}`, "aria-label": t('rotation_3d.aria.candidate'), role: "img", children: _jsx("polygon", { points: item.candPoints, fill: SHAPE_FILL, stroke: SHAPE_STROKE, strokeWidth: 1.5, strokeLinejoin: "round" }) })] }), pick === null && (_jsxs("div", { className: "flex items-center justify-center gap-s3", children: [_jsx("button", { type: "button", onClick: () => handlePick('identical'), className: "inline-flex h-12 items-center justify-center bg-cyan-core px-s5 font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: t('rotation_3d.buttons.identical') }), _jsx("button", { type: "button", onClick: () => handlePick('mirror'), className: "inline-flex h-12 items-center justify-center border border-cyan-border bg-cyan-surface px-s5 font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-text transition active:scale-[0.985] hover:bg-cyan-bg", children: t('rotation_3d.buttons.mirror') })] })), pick !== null && (_jsx("p", { className: [
                    'text-[13px]',
                    correct ? 'text-cyan-core' : 'text-cyan-media',
                ].join(' '), children: correct
                    ? t('rotation_3d.demo.feedback_correct')
                    : t('rotation_3d.demo.feedback_incorrect', {
                        truth: correctPick === 'identical'
                            ? t('rotation_3d.buttons.identical')
                            : t('rotation_3d.buttons.mirror'),
                    }) })), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [pick !== null && (_jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? t('common:actions.start_session') : t('common:actions.next_trial') })), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.skip_demo') })] })] }));
}
//# sourceMappingURL=demo.js.map